import {
  autoUpdate,
  ClientRectObject,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import useResizeObserver from '@react-hook/resize-observer';
import React, { MouseEvent, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme as useStyledTheme } from 'styled-components';
import { useOnClickOutside } from 'usehooks-ts';

import { useTheme } from '../../state/theme-state';
import { NotificationBellStatus, useWebsocketState } from '../../state/websocket-state';
import { Icon } from '../icon/icon';
import { Stack } from '../stack/stack';

function resolveIconName(status: NotificationBellStatus) {
  if (status === 'messageRecieved') {
    return 'bell-ring';
  }

  return 'bell';
}

function resolveColor(status: NotificationBellStatus, theme: ReturnType<typeof useStyledTheme>) {
  if (status === 'disconnected') {
    return theme.color.red;
  }

  if (status === 'connecting') {
    return theme.color.gray.medium;
  }

  return undefined;
}

const isWithinRect = (
  event: { clientX?: number; clientY?: number },
  rect: DOMRect | ClientRectObject | undefined,
) => {
  if (rect == null || event?.clientX == null || event?.clientY == null) {
    return false;
  }

  return (
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  );
};

export const NotificationBellIcon = () => {
  const styledTheme = useStyledTheme();
  const themeContext = useTheme();
  const topMenuTextColor = themeContext.getTopMenuTextColor();
  const topMenuTextHoverColor = themeContext.getTopMenuTextHoverColor();
  const { t } = useTranslation();
  const {
    status,
    notifications,
    notificationsOpen,
    setNotificationsOpen,
    acknowledgeMessages,
    clearNotification,
    clearNotifications,
    connect,
  } = useWebsocketState();

  const { refs, strategy, floatingStyles, update } = useFloating({
    middleware: [offset(), flip(), shift()],
    placement: 'bottom-end',
  });
  const itemCount = useMemo(
    () => (notifications.length > 9 ? '9+' : notifications.length.toString()),
    [notifications],
  );

  useResizeObserver(document.body, () => {
    if (refs.reference.current && refs.floating.current) {
      autoUpdate(refs.reference.current, refs.floating.current, update);
    }
  });

  useOnClickOutside(refs.floating as React.RefObject<HTMLElement>, (e) => {
    if (
      notificationsOpen &&
      !isWithinRect(e as globalThis.MouseEvent, refs.reference.current?.getBoundingClientRect())
    ) {
      setNotificationsOpen(false);
    }
  });

  const onClick = useCallback(
    (e: MouseEvent) => {
      if (status === 'disconnected') {
        connect();
        return;
      }

      if (status === 'messageRecieved') {
        acknowledgeMessages();
      }

      if (isWithinRect(e, refs.reference.current?.getBoundingClientRect())) {
        setNotificationsOpen((state) => !state);
      }
    },
    [acknowledgeMessages, connect, refs.reference, setNotificationsOpen, status],
  );

  return (
    <Component
      aria-label={t('notifications.ariaLabel', { ns: 'navigation' })}
      data-testid="notification-bell"
      highlighted={notificationsOpen}
      ref={refs.setReference}
      textColor={topMenuTextColor}
      textHoverColor={topMenuTextHoverColor}
      onClick={onClick}
    >
      <IconContainer>
        <Icon
          beatFade={status === 'connecting'}
          color={resolveColor(status, styledTheme)}
          fixedWidth={true}
          icon={['fas', resolveIconName(status)]}
          size={'lg'}
          title={t('notifications.title', { ns: 'components' })}
          onHoverStyle={{ icon: ['fad', resolveIconName(status)] }}
        />
        {notifications.length > 0 && (
          <MessageCountLabel data-testid="notification-count" status={status}>
            {itemCount}
          </MessageCountLabel>
        )}
      </IconContainer>

      {notificationsOpen && (
        <FloatingPortal>
          <NotificationList
            className="notranslate"
            data-testid="notification-list"
            ref={refs.setFloating}
            style={{ ...floatingStyles, position: strategy }}
          >
            <NotificationsHeader alignItems="center" direction="row" justifyContent="space-between">
              <label>{t('notifications.title', { ns: 'components' })}</label>
              <ClearButton
                aria-label={t('notifications.clearAll', { ns: 'components' })}
                type="button"
                onClick={clearNotifications}
              >
                <Icon
                  color={styledTheme.color.gray.dark}
                  fixedWidth={true}
                  icon={['fal', 'broom-wide']}
                  size={'lg'}
                  title={t('notifications.clearAll', { ns: 'components' })}
                  onHoverStyle={{ icon: ['fad', 'broom-wide'] }}
                />
              </ClearButton>
            </NotificationsHeader>

            {notifications.length === 0 && (
              <NotificationCard alignItems="center" direction="column" spacing={0.5}>
                <Icon
                  color={styledTheme.color.gray.dark}
                  fixedWidth={true}
                  icon={['fad', 'bell']}
                  size={'xl'}
                />
                <NoNewNotifications>
                  {t('notifications.noNewNotifications', { ns: 'components' })}
                </NoNewNotifications>
              </NotificationCard>
            )}

            <Stack direction="column">
              {notifications.map((item, idx) => (
                <NotificationCard direction="column" key={idx} spacing={0.25}>
                  <Stack alignItems="center" direction="row" justifyContent="space-between">
                    <TimeStamp>{item.timestamp}</TimeStamp>
                    <Icon
                      color={styledTheme.color.gray.dark}
                      fixedWidth={true}
                      icon={['fas', 'xmark']}
                      size={'lg'}
                      onClick={() => clearNotification(item.id)}
                      onHoverStyle={{ icon: ['fad', 'xmark'] }}
                    />
                  </Stack>
                  <Stack alignItems="center" direction="row" spacing={0.5}>
                    {item.icon}
                    <CardContent>{item.content}</CardContent>
                  </Stack>
                </NotificationCard>
              ))}
            </Stack>
          </NotificationList>
        </FloatingPortal>
      )}
    </Component>
  );
};

const Component = styled.button<{
  highlighted: boolean;
  textColor?: string;
  textHoverColor?: string;
}>`
  color: ${(props) => props.textColor ?? 'inherit'};
  padding: 0 15px;
  position: relative;
  align-content: center;
  height: 100%;
  background: none;
  border: 0;
  cursor: pointer;
  &:hover {
    background-color: ${(props) => props.theme.color.gray.darkest};
    color: ${(props) => props.textHoverColor ?? props.textColor ?? 'inherit'};
  }
  background-color: ${(props) => (props.highlighted ? props.theme.color.gray.darkest : 'unset')};
`;

const ClearButton = styled.button`
  border: none;
  background: none;
  color: ${(props) => props.theme.color.gray.dark};
  text-decoration: underline;
  cursor: pointer;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const IconContainer = styled.div`
  position: relative;
  user-select: none;
`;

const MessageCountLabel = styled.label<{ status: NotificationBellStatus }>`
  position: absolute;
  font-size: 0.65em;
  padding: 0.15em 0.35em;
  line-height: 1;
  user-select: none;
  text-align: center;
  min-width: 1em;
  color: white;
  background-color: ${(props) =>
    props.status === 'messageRecieved' ? 'red' : props.theme.color.gray.medium};
  border-radius: 1em;
  bottom: 0;
  right: -0.5em;
`;

const NotificationList = styled.div`
  margin: 0;
  padding: 0;
  list-style: none;
  background: ${(props) => props.theme.color.gray.lightest};
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);
  overflow: hidden;
  overflow-y: auto;
  max-height: 40%;
  z-index: 1000;
  width: 350px;
  box-sizing: border-box;
`;

const CardContent = styled.div`
  flex: 1;
`;

const NotificationsHeader = styled(Stack)`
  position: sticky;
  top: 0;
  z-index: 1;
  background-color: white;
  padding: 1em 0.5em;
  color: ${(props) => props.theme.color.gray.dark};
  user-select: none;
  border-bottom: 1px solid ${(props) => props.theme.color.gray.lightest};
`;

const NoNewNotifications = styled.label`
  color: ${(props) => props.theme.color.gray.medium};
  user-select: none;
`;

const TimeStamp = styled.div`
  color: ${(props) => props.theme.color.gray.medium};
  font-size: 0.75em;
`;

const NotificationCard = styled(Stack)`
  position: relative;
  margin: 0.15em 0.5em;
  padding: 0.5em;
  background-color: white;
  border: 1px solid ${(props) => props.theme.color.gray.medium};
  border-radius: 6px;
  &:hover {
    background-color: ${(props) => props.theme.color.brand.lightest};
  }
`;
